import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { Form, Row, Col } from 'antd'
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction'

import AnnualPhysicalForm from 'model/wellness/annualPhysical'
import { Label } from 'pages/Ticket/styled'
import FullScreenLoader from 'components/FullScreenLoader'
import Attachments from 'pages/Ticket/components/RequestData/components/Attachments'
import { Select, Input, Button } from 'ui/styled'
import ANNUAL_PHYSICAL_INPUTS from 'apollo/queries/Wellness/ANNUAL_PHYSICAL_INPUTS'
import useAnnualUploadAttachment from 'apollo/mutations/Wellness/ANNUAL_PHYSICAL_UPLOAD_ATTACHMENT'
import useAnnualFormUpdate from 'apollo/mutations/Wellness/ANNUAL_PHYSICAL_UPDATE'
import useAnnualUpdateAttachment from 'apollo/mutations/Wellness/ANNUAL_PHYSICAL_UPDATE_ATTACHMENT'
import useAnnualDeleteAttachment from 'apollo/mutations/Wellness/ANNUAL_PHYSICAL_DELETE_ATTACHMENT'
import NewAttachment from './components/NewAttachment'
import { FormItem } from './styled'

const { Item } = Form

const generateOptions = (array = []) =>
  array.map(i => ({
    value: i,
    label: i
  }))

const AnnualPhysicalFields = ({
  form = {},
  annualPhysical = false,
  disabled = false
}) => {
  const [newAttachment, setNewAttachment] = useState(null)
  const { data, loading } = useQuery(ANNUAL_PHYSICAL_INPUTS)

  const { onAnnualAttachmentUpload } = useAnnualUploadAttachment()
  const { onAnnualFormUpdate } = useAnnualFormUpdate()
  const { onAnnualAttachmentUpdate } = useAnnualUpdateAttachment()
  const { onAnnualAttachmentDelete } = useAnnualDeleteAttachment()

  const attachToForm = async file => {
    if (!annualPhysical) return false

    const { isErrorFree } = await onAnnualAttachmentUpload({
      id: annualPhysical.id,
      attachment: {
        file,
        title: form.getFieldValue(['attachments', 0, 'title']),
        notes: form.getFieldValue(['attachments', 0, 'notes'])
      }
    })
    if (!isErrorFree) return false
    setNewAttachment(false)
    form.setFieldsValue({ attachments: null })
  }

  const onRemoveAttachment = () => {
    setNewAttachment(false)
    form.setFieldsValue({ attachments: [] })
  }

  const onAddAttachment = () => {
    setNewAttachment(true)
  }

  const updateAnnualPhysical = useCallback(
    async (key, value) => {
      if (!annualPhysical) return false

      await onAnnualFormUpdate({
        id: annualPhysical.id,
        attributes: { [key]: value }
      })
    },
    [annualPhysical.id, onAnnualFormUpdate]
  )

  const debouncedUpdateAnnualPhysical = useDebouncedFunction(
    updateAnnualPhysical,
    500
  )

  const attachUpdateHandler = async attachments => {
    if (!attachments || !attachments.length) return false

    await onAnnualAttachmentUpdate({
      id: attachments[0].id,
      attachment: {
        notes: attachments[0].notes,
        title: attachments[0].title
      }
    })
  }

  const attachRemoveHandler = async id => {
    await onAnnualAttachmentDelete({ id })
  }

  if (loading || !data) return <FullScreenLoader />

  const {
    input_annual_physical_form: {
      patient_name,
      mammogram,
      pap_smear,
      prostate_screening,
      colon_screening,
      smoking_attestation
    }
  } = data

  const options = {
    mammogram: generateOptions(mammogram.answers),
    pap_smear: generateOptions(pap_smear.answers),
    prostate_screening: generateOptions(prostate_screening.answers),
    colon_screening: generateOptions(colon_screening.answers),
    smoking_attestation: generateOptions(smoking_attestation.answers)
  }

  const { form_attachment } = annualPhysical

  return (
    <>
      <Label>Wellness Form</Label>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Label required>Patient Name (Employee or Spouse)</Label>
          <FormItem
            name="patient_name"
            rules={[
              {
                required: true,
                message: 'Please input patient name'
              }
            ]}
            onChange={
              annualPhysical
                ? e =>
                    debouncedUpdateAnnualPhysical(
                      'patient_name',
                      e.target.value
                    )
                : undefined
            }
          >
            <Input
              placeholder={patient_name?.placeholder}
              disabled={disabled}
            />
          </FormItem>
        </Col>
      </Row>
      <Label>Screenings Required for Women</Label>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Label required>Mammogram</Label>
          <FormItem
            name="mammogram"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              disabled={disabled}
              options={options.mammogram}
              placeholder={mammogram?.placeholder}
              onChange={
                annualPhysical
                  ? val => updateAnnualPhysical('mammogram', val)
                  : undefined
              }
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <Label required>Pap Smear</Label>
          <FormItem
            name="pap_smear"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              disabled={disabled}
              options={options.pap_smear}
              placeholder={pap_smear?.placeholder}
              onChange={
                annualPhysical
                  ? val => updateAnnualPhysical('pap_smear', val)
                  : undefined
              }
            />
          </FormItem>
        </Col>
      </Row>

      <Label>Screenings Required for Men</Label>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Label required>Prostate Cancer Screening</Label>
          <FormItem
            name="prostate_screening"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              disabled={disabled}
              options={options.prostate_screening}
              placeholder={prostate_screening?.placeholder}
              onChange={
                annualPhysical
                  ? val => updateAnnualPhysical('prostate_screening', val)
                  : undefined
              }
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Label required>Colon Screening</Label>
          <FormItem
            name="colon_screening"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              disabled={disabled}
              options={options.colon_screening}
              placeholder={colon_screening?.placeholder}
              onChange={
                annualPhysical
                  ? val => updateAnnualPhysical('colon_screening', val)
                  : undefined
              }
            />
          </FormItem>
        </Col>
      </Row>

      <Label>Smoking Attestation</Label>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Label required>Have you used tobacco in the past 3 months?</Label>
          <Item
            name="smoking_attestation"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              disabled={disabled}
              options={options.smoking_attestation}
              placeholder={smoking_attestation?.placeholder}
              onChange={
                annualPhysical
                  ? val => updateAnnualPhysical('smoking_attestation', val)
                  : undefined
              }
            />
          </Item>
        </Col>
      </Row>

      <Label required>Attachment</Label>
      {annualPhysical && (
        <Attachments
          key={form_attachment?.id}
          attachments={[form_attachment]}
          updateHandler={attachUpdateHandler}
          removeHandler={attachRemoveHandler}
          disabled={disabled}
        />
      )}

      {newAttachment && (
        <NewAttachment
          key={newAttachment}
          index={0}
          attachHandler={attachToForm}
          onRemoveAttachment={onRemoveAttachment}
          isSingleFile
          disabled={disabled}
        />
      )}

      <Button
        onClick={onAddAttachment}
        disabled={!!newAttachment || form_attachment || disabled}
        data-test="ticketAddAttachmentBtn"
      >
        Add New Attachment
      </Button>
    </>
  )
}

AnnualPhysicalFields.propTypes = {
  annualPhysical: PropTypes.shape(AnnualPhysicalForm.shape),
  form: PropTypes.object,
  disabled: PropTypes.bool
}

export default AnnualPhysicalFields
